import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBatchLedgers } from '@/api/hooks/useBatchLedgers';
import { useLedgerExport } from '@/hooks/useLedgerExport';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Spinner } from '@/components/ui/Spinner';
import { LedgerFilters } from '@/components/ledgers/LedgerFilters';
import { LedgersSummaryCard } from '@/components/ledgers/LedgersSummaryCard';
import { AggregatedValueChart } from '@/components/ledgers/AggregatedValueChart';
import { AggregatedPnlChart } from '@/components/ledgers/AggregatedPnlChart';
import { AggregatedReturnsChart } from '@/components/ledgers/AggregatedReturnsChart';
import { getErrorMessage } from '@/utils/errorHandler';
import type { LedgerType, CollectionWithTotal, AnalyticsMode, AnalyticsGranularity } from '@/api/types';

// localStorage keys for fallback defaults
const STORAGE_KEY_GRANULARITY = 'granularity-aggregated';
const STORAGE_KEY_VIEW_MODE = 'view-mode-aggregated';

// Helper to get granularity from URL with localStorage fallback
function getGranularityFromParams(searchParams: URLSearchParams): AnalyticsGranularity {
    const urlValue = searchParams.get('granularity');
    if (urlValue === 'month' || urlValue === 'year') return urlValue;
    try {
        const stored = localStorage.getItem(STORAGE_KEY_GRANULARITY);
        if (stored === 'month' || stored === 'year') return stored;
    } catch {
        // localStorage unavailable
    }
    return 'month';
}

// Helper to get viewMode from URL with localStorage fallback
function getViewModeFromParams(searchParams: URLSearchParams): 'chart' | 'table' {
    const urlValue = searchParams.get('display');
    if (urlValue === 'chart' || urlValue === 'table') return urlValue;
    try {
        const stored = localStorage.getItem(STORAGE_KEY_VIEW_MODE);
        if (stored === 'chart' || stored === 'table') return stored;
    } catch {
        // localStorage unavailable
    }
    return 'chart';
}

interface LedgersAnalyticsViewProps {
    type: LedgerType;
    /** Callback to set header action (download button) */
    onHeaderAction?: (node: ReactNode) => void;
}

/**
 * Analytics view component for displaying ledger analytics.
 * Shows an aggregated value chart, P&L chart, or MWRR chart
 * across all matching ledgers with collection/tag filters.
 *
 * For assets: Shows ModeToggle to switch between Value, P&L, and MWRR views.
 * For debts: Only shows Value view (P&L, MWRR are not meaningful for debts).
 */
export function LedgersAnalyticsView({ type, onHeaderAction }: LedgersAnalyticsViewProps) {
    const { updateFromBatch } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const typeLabelPlural = type === 'asset' ? 'Assets' : 'Debts';

    // Analytics mode (value, pnl, or mwrr) - only for assets
    const [mode, setMode] = useState<AnalyticsMode>(() => {
        const urlMode = searchParams.get('mode');
        if (type === 'asset') {
            if (urlMode === 'pnl') return 'pnl';
            if (urlMode === 'mwrr') return 'mwrr';
        }
        return 'value';
    });

    // Parse URL params for initial state
    const parseNumberArray = (param: string | null): number[] => {
        if (!param) return [];
        return param.split(',').map(Number).filter(n => !isNaN(n));
    };

    // Filter state - synced with URL params (no archived filter in analytics view)
    const [selectedCollectionIds, setSelectedCollectionIds] = useState<number[]>(() =>
        parseNumberArray(searchParams.get('collections'))
    );
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>(() =>
        parseNumberArray(searchParams.get('tags'))
    );
    // Pending tags - updated on each selection, applied on dropdown close
    const [pendingTagIds, setPendingTagIds] = useState<number[]>(() =>
        parseNumberArray(searchParams.get('tags'))
    );

    // Track chart fetching state for loading overlay during mode switches
    const [chartFetching, setChartFetching] = useState(false);

    // URL-based granularity with localStorage fallback
    const granularity = getGranularityFromParams(searchParams);

    // URL-based viewMode with localStorage fallback
    const viewMode = getViewModeFromParams(searchParams);

    // Reset mode to 'value' when switching to debt type
    useEffect(() => {
        if (type === 'debt' && mode !== 'value') {
            setMode('value');
        }
    }, [type, mode]);

    // Helper to update URL params while preserving current values
    const updateSingleUrlParam = useCallback((key: string, value: string) => {
        const params = new URLSearchParams(searchParams);
        params.set(key, value);
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // Sync state changes to URL
    const updateUrlParams = useCallback((
        collections: number[],
        tags: number[],
        currentMode: AnalyticsMode
    ) => {
        const params = new URLSearchParams(searchParams);

        params.delete('collections');
        params.delete('tags');
        params.delete('mode');

        if (collections.length > 0) {
            params.set('collections', collections.join(','));
        }
        if (tags.length > 0) {
            params.set('tags', tags.join(','));
        }
        // Persist mode in URL for assets (if not value)
        if (type === 'asset' && currentMode !== 'value') {
            params.set('mode', currentMode);
        }
        // Keep view param
        params.set('view', 'analytics');
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams, type]);

    // Wrapped setters that also update URL
    const handleCollectionChange = useCallback((ids: number[]) => {
        setSelectedCollectionIds(ids);
        updateUrlParams(ids, selectedTagIds, mode);
    }, [selectedTagIds, mode, updateUrlParams]);

    // Update pending tags on each selection (UI only, no chart update)
    const handleTagsChange = useCallback((ids: number[]) => {
        setPendingTagIds(ids);
    }, []);

    // Apply pending tags on dropdown close (triggers chart update)
    const handleTagsClose = useCallback(() => {
        setSelectedTagIds(pendingTagIds);
        updateUrlParams(selectedCollectionIds, pendingTagIds, mode);
    }, [pendingTagIds, selectedCollectionIds, mode, updateUrlParams]);

    // Handle mode change
    const handleModeChange = useCallback((newMode: AnalyticsMode) => {
        setMode(newMode);
        updateUrlParams(selectedCollectionIds, selectedTagIds, newMode);
    }, [selectedCollectionIds, selectedTagIds, updateUrlParams]);

    // Handle granularity change - update URL and persist to localStorage
    const handleGranularityChange = useCallback((newGranularity: AnalyticsGranularity) => {
        try {
            localStorage.setItem(STORAGE_KEY_GRANULARITY, newGranularity);
        } catch {
            // localStorage unavailable
        }
        updateSingleUrlParam('granularity', newGranularity);
    }, [updateSingleUrlParam]);

    // Handle viewMode change - update URL and persist to localStorage
    const handleViewModeChange = useCallback((newViewMode: 'chart' | 'table') => {
        try {
            localStorage.setItem(STORAGE_KEY_VIEW_MODE, newViewMode);
        } catch {
            // localStorage unavailable
        }
        updateSingleUrlParam('display', newViewMode);
    }, [updateSingleUrlParam]);

    // Reset all filters (preserve granularity, viewMode, and mode)
    const handleResetFilters = useCallback(() => {
        setSelectedCollectionIds([]);
        setSelectedTagIds([]);
        setPendingTagIds([]);
        const params = new URLSearchParams(searchParams);
        params.delete('collections');
        params.delete('tags');
        params.set('view', 'analytics');
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // Switch to list view (for empty state actions)
    const handleSwitchToList = useCallback(() => {
        const params = new URLSearchParams(searchParams);
        params.set('view', 'list');
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // Fetch data (always includes archived for correct P&L calculation)
    const {
        data: batchData,
        isLoading,
        error,
    } = useBatchLedgers({ type });

    // Update AuthContext with batch data when it loads
    useEffect(() => {
        if (batchData?.profile?.user && batchData?.profile?.settings) {
            updateFromBatch(batchData.profile.user, batchData.profile.settings);
        }
    }, [batchData, updateFromBatch]);

    // Get ledgers, collections, and tags
    const allLedgers = useMemo(
        () => batchData?.ledgers?.items || [],
        [batchData?.ledgers?.items]
    );
    const collections = useMemo(
        () => batchData?.collections?.items || [],
        [batchData?.collections?.items]
    );
    const tags = useMemo(
        () => batchData?.tags?.items || [],
        [batchData?.tags?.items]
    );

    // Calculate total from ALL non-archived ledgers (for percentage calculation)
    // Archived ledgers should not contribute to total value
    const allLedgersTotal = useMemo(() => {
        return allLedgers
            .filter(l => !l.archivedAt)
            .reduce((sum, l) => sum + (l.latestValue?.convertedValue ?? 0), 0);
    }, [allLedgers]);

    // Compute collection totals
    const collectionsWithTotals: CollectionWithTotal[] = useMemo(() => {
        return collections.map((collection) => {
            const collectionLedgers = allLedgers.filter(
                (l) => l.collectionId === collection.id
            );
            const total = collectionLedgers.reduce(
                (sum, l) => sum + (l.latestValue?.convertedValue ?? 0),
                0
            );
            return {
                ...collection,
                total,
                ledgerCount: collectionLedgers.length,
            };
        });
    }, [collections, allLedgers]);

    // Apply client-side filters (analytics view never shows archived ledgers)
    const filteredLedgers = useMemo(() => {
        // Filter out archived ledgers from display
        let result = allLedgers.filter(l => !l.archivedAt);

        // Filter by collections (multi-select, empty = all)
        if (selectedCollectionIds.length > 0) {
            result = result.filter((l) => selectedCollectionIds.includes(l.collectionId));
        }

        // Filter by tags (show ledgers having ANY selected tag)
        if (selectedTagIds.length > 0) {
            result = result.filter((l) =>
                l.tagIds.some((tagId) => selectedTagIds.includes(tagId))
            );
        }

        return result;
    }, [allLedgers, selectedCollectionIds, selectedTagIds]);

    // Calculate filtered totals from ledgers (always calculated on frontend now)
    const hasClientFilters = selectedCollectionIds.length > 0 || selectedTagIds.length > 0;

    const { filteredTotal, filteredProfitLoss } = useMemo(() => {
        let total = 0;
        let profitLoss = 0;
        let hasAnyPnl = false;

        // Get ledgers matching filters (including archived) for P&L calculation
        let matchingLedgers = allLedgers;

        if (selectedCollectionIds.length > 0) {
            matchingLedgers = matchingLedgers.filter(
                l => selectedCollectionIds.includes(l.collectionId)
            );
        }

        if (selectedTagIds.length > 0) {
            matchingLedgers = matchingLedgers.filter(
                l => l.tagIds.some(tagId => selectedTagIds.includes(tagId))
            );
        }

        // Calculate P&L from matching ledgers (including archived)
        for (const ledger of matchingLedgers) {
            if (ledger.analytics?.profitLoss != null) {
                profitLoss += ledger.analytics.profitLoss;
                hasAnyPnl = true;
            }
        }

        // Calculate total value from filtered ledgers (already excludes archived)
        for (const ledger of filteredLedgers) {
            total += ledger.latestValue?.convertedValue ?? 0;
        }

        return {
            filteredTotal: total,
            filteredProfitLoss: hasAnyPnl ? profitLoss : null,
        };
    }, [allLedgers, filteredLedgers, selectedCollectionIds, selectedTagIds]);

    // Calculate percentage of total when filters are active
    const percentageOfTotal = useMemo(() => {
        if (!hasClientFilters || allLedgersTotal === 0) {
            return null; // No percentage when showing everything or total is 0
        }
        return (filteredTotal / allLedgersTotal) * 100;
    }, [hasClientFilters, filteredTotal, allLedgersTotal]);

    // Determine if filters are applied
    const hasActiveFilters = selectedCollectionIds.length > 0 || selectedTagIds.length > 0;

    // Show mode toggle only for assets
    const showModeToggle = type === 'asset';

    // Set up header export button
    useLedgerExport({ ledgers: filteredLedgers, type, onHeaderAction });

    // Error state
    if (error) {
        return <ErrorAlert message={getErrorMessage(error)} />;
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <div className="text-gray-500 dark:text-gray-400">
                    Loading analytics...
                </div>
            </div>
        );
    }

    // No Collections Empty State
    if (collections.length === 0) {
        return (
            <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary p-8 text-center">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                    No {type === 'asset' ? 'asset' : 'debt'} collections yet.
                </p>
                <p className="text-gray-500 dark:text-gray-500 mb-6">
                    Create a collection first to start tracking your {typeLabelPlural.toLowerCase()}.
                </p>
                <Link
                    to="/settings/attribution"
                    className="inline-flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
                >
                    Manage Collections
                </Link>
            </div>
        );
    }

    // Render the appropriate chart based on mode
    const renderChart = () => {
        switch (mode) {
            case 'pnl':
                return (
                    <AggregatedPnlChart
                        collectionIds={selectedCollectionIds}
                        tagIds={selectedTagIds}
                        onResetFilters={handleResetFilters}
                        mode={mode}
                        onModeChange={handleModeChange}
                        granularity={granularity}
                        onGranularityChange={handleGranularityChange}
                        viewMode={viewMode}
                        onViewModeChange={handleViewModeChange}
                        onFetchingChange={setChartFetching}
                    />
                );
            case 'mwrr':
                return (
                    <AggregatedReturnsChart
                        collectionIds={selectedCollectionIds}
                        tagIds={selectedTagIds}
                        onResetFilters={handleResetFilters}
                        mode={mode}
                        onModeChange={handleModeChange}
                        granularity={granularity}
                        onGranularityChange={handleGranularityChange}
                        viewMode={viewMode}
                        onViewModeChange={handleViewModeChange}
                        onFetchingChange={setChartFetching}
                    />
                );
            case 'value':
            default:
                return (
                    <AggregatedValueChart
                        type={type}
                        collectionIds={selectedCollectionIds}
                        tagIds={selectedTagIds}
                        onResetFilters={handleResetFilters}
                        mode={showModeToggle ? mode : undefined}
                        onModeChange={showModeToggle ? handleModeChange : undefined}
                        granularity={granularity}
                        onGranularityChange={handleGranularityChange}
                        viewMode={viewMode}
                        onViewModeChange={handleViewModeChange}
                        onFetchingChange={setChartFetching}
                        onSwitchToList={handleSwitchToList}
                    />
                );
        }
    };

    return (
        <>
            {/* Filters (no archived toggle) */}
            <LedgerFilters
                collections={collectionsWithTotals}
                tags={tags}
                selectedCollectionIds={selectedCollectionIds}
                selectedTagIds={pendingTagIds}
                showArchived={false}
                onCollectionChange={handleCollectionChange}
                onTagsChange={handleTagsChange}
                onTagsClose={handleTagsClose}
                onShowArchivedChange={() => {}} // No-op, archived not used
                hasArchivedLedgers={false}
                hasActiveFilters={hasActiveFilters}
                onReset={handleResetFilters}
                hideArchivedToggle
                type={type}
            />

            <LedgersSummaryCard
                type={type}
                filteredTotal={filteredTotal}
                percentageOfTotal={percentageOfTotal}
                filteredProfitLoss={filteredProfitLoss}
            />

            {/* Chart based on mode */}
            <div className="relative">
                {chartFetching && (
                    <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-10 rounded-lg">
                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                            <Spinner size="sm" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">Updating...</span>
                        </div>
                    </div>
                )}
                {renderChart()}
            </div>

        </>
    );
}
